import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  fetchEmailTemplatesList,
  fetchCategoryStats,
  unsentContactCount,
  sendTestEmail,
} from "../../api/contacts";
import { useAuth } from "../../context/AuthContext";
import TemplatePreviewModal from "../../components/TemplatePreviewModal";
import { FiMail, FiSend } from "react-icons/fi";
import { toast } from "react-hot-toast";
import QuickSend from "./methods/QuickSend";
import CategorySend from "./methods/CategorySend";
import UnsentSend from "./methods/UnsentSend";
import SelectedContactsSend from "./methods/SelectedContactsSend";
import FollowUpSequenceCard from "./FollowUpSequenceCard";

export default function SendEmail() {
  const { token, user } = useAuth();
  const location = useLocation();
  const preSelectedIds = location.state?.selectedContactIds || [];

  const [method, setMethod] = useState(preSelectedIds.length > 0 ? "selected" : "unsent");
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [unsentCount, setUnsentCount] = useState(0);

  const [testEmailAddr, setTestEmailAddr] = useState(user?.email || "");
  const [sendingTest, setSendingTest] = useState(false);
  const [savedTestEmails, setSavedTestEmails] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem("saved_test_emails");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSavedTestEmails(parsed);
          setTestEmailAddr((prev) => prev || parsed[0]);
        }
      } catch (e) {}
    }
  }, [user]);


  /* Load Templates */
  useEffect(() => {
    if (!token?.access) return;
    fetchEmailTemplatesList(token.access).then(setTemplates);
  }, [token]);

  /* Load Categories */
  useEffect(() => {
    if (!token?.access) return;
    fetchCategoryStats(token.access).then(setCategories);
  }, [token]);

  /* Load Unsent Count */
  const loadUnsentCount = useCallback(async () => {
    if (!token?.access) return;
    const count = await unsentContactCount(token.access);
    setUnsentCount(count);
  }, [token]);

  useEffect(() => {
    loadUnsentCount();
  }, [loadUnsentCount]);

  const handleSendTest = async () => {
    if (!testEmailAddr) {
      toast.error("Please enter a test email address!");
      return;
    }
    setSendingTest(true);
    try {
      const res = await sendTestEmail(token.access, testEmailAddr, templateId);
      toast.success(res.message || "Test email sent successfully!");
    } catch (err) {
      toast.error(err.message || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const [trackOpens, setTrackOpens] = useState(true); // Open tracking active
  const [includeUnsubscribe, setIncludeUnsubscribe] = useState(false); // Default false for clean 1-to-1 look

  return (
    <div className="p-6 mx-auto max-w-7xl">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-6 text-gray-900">
        <FiMail className="text-blue-600" /> Send Email
      </h1>

      {/* DELIVERABILITY MODE CARD */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 p-4.5 rounded-xl shadow-xs mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
              🛡️ Primary Inbox Deliverability Mode Active
            </span>
            <p className="text-xs text-emerald-900 mt-1">
              Emails are formatted with clean 1-to-1 human typography without bulk marketing wrappers or tracking pixels that trigger Gmail&apos;s Promotions tab.
            </p>
          </div>

          <div className="flex items-center gap-4 text-xs font-medium text-gray-700 bg-white/80 backdrop-blur-xs p-2.5 rounded-lg border border-emerald-200/80">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={trackOpens}
                onChange={(e) => setTrackOpens(e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span>Attach Open Pixel</span>
            </label>

            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeUnsubscribe}
                onChange={(e) => setIncludeUnsubscribe(e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span>Include Opt-Out Link</span>
            </label>
          </div>
        </div>
      </div>

      {/* TEMPLATE SELECT & TEST EMAIL */}
      <div className="bg-white p-6 rounded-xl shadow-xs border border-gray-200 mb-6 space-y-4">
        <div>
          <label className="text-sm font-semibold text-gray-700">1. Select Email Template</label>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="w-full border border-gray-300 p-2.5 rounded-lg mt-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
          >
            <option value="">-- Select Template --</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} - {t.subject}
              </option>
            ))}
          </select>

          {templateId && (
            <button
              className="mt-2 text-blue-600 text-sm font-medium hover:underline inline-flex items-center gap-1"
              onClick={() => setPreview(templates.find((t) => t.id === Number(templateId)))}
            >
              🔍 Preview Selected Template
            </button>
          )}
        </div>

        <hr className="border-gray-100" />

        {/* TEST EMAIL BOX */}
        <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex-1">
            <label className="text-xs font-bold text-blue-900 uppercase tracking-wider">
              🧪 Quick Test Email Before Sending Campaign
            </label>
            <input
              type="email"
              value={testEmailAddr}
              onChange={(e) => setTestEmailAddr(e.target.value)}
              placeholder="Enter your email to test (e.g. test@gmail.com)"
              className="w-full border border-gray-300 p-2 rounded-lg mt-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {savedTestEmails.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className="text-[11px] font-semibold text-blue-800">Saved:</span>
                {savedTestEmails.map((email, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setTestEmailAddr(email)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                      testEmailAddr === email
                        ? "bg-blue-600 text-white border-blue-600 font-semibold"
                        : "bg-white text-blue-700 border-blue-200 hover:bg-blue-100"
                    }`}
                  >
                    {email}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={handleSendTest}
            disabled={sendingTest}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 transition disabled:opacity-50 shadow-xs"
          >
            <FiSend size={16} />
            {sendingTest ? "Sending Test..." : "Send Test Email"}
          </button>
        </div>
      </div>

      {/* METHOD SELECTOR */}
      <div className="bg-white p-6 rounded-xl shadow-xs border border-gray-200 mb-6">
        <label className="text-sm font-semibold text-gray-700">2. Select Target Audience Method</label>
        <div className="flex flex-wrap gap-6 mt-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
            <input
              type="radio"
              value="unsent"
              checked={method === "unsent"}
              onChange={() => setMethod("unsent")}
              className="text-blue-600"
            />
            Unsent Contacts ({unsentCount})
          </label>

          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
            <input
              type="radio"
              value="quick"
              checked={method === "quick"}
              onChange={() => setMethod("quick")}
              className="text-blue-600"
            />
            Quick Manual Send
          </label>

          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
            <input
              type="radio"
              value="category"
              checked={method === "category"}
              onChange={() => setMethod("category")}
              className="text-blue-600"
            />
            Send by Category
          </label>

          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
            <input
              type="radio"
              value="selected"
              checked={method === "selected"}
              onChange={() => setMethod("selected")}
              className="text-blue-600"
            />
            Select Specific Contacts ⭐
          </label>
        </div>
      </div>

      {/* OPTIONAL AUTOMATIC FOLLOW-UPS CARD */}
      <FollowUpSequenceCard token={token} templates={templates} />

      {/* RENDER SELECTED METHOD COMPONENT */}
      {method === "quick" && (
        <QuickSend
          token={token}
          templateId={templateId}
          setResult={setResult}
          trackOpens={trackOpens}
          includeUnsubscribe={includeUnsubscribe}
        />
      )}

      {method === "category" && (
        <CategorySend
          token={token}
          templateId={templateId}
          categories={categories}
          setResult={setResult}
          trackOpens={trackOpens}
          includeUnsubscribe={includeUnsubscribe}
        />
      )}

      {method === "unsent" && (
        <UnsentSend
          token={token}
          templateId={templateId}
          setResult={setResult}
          refreshUnsent={loadUnsentCount}
          trackOpens={trackOpens}
          includeUnsubscribe={includeUnsubscribe}
        />
      )}

      {method === "selected" && (
        <SelectedContactsSend
          token={token}
          templateId={templateId}
          setResult={setResult}
          initialSelectedIds={preSelectedIds}
          trackOpens={trackOpens}
          includeUnsubscribe={includeUnsubscribe}
        />
      )}

      {/* TEMPLATE PREVIEW */}
      {preview && (
        <TemplatePreviewModal template={preview} onClose={() => setPreview(null)} />
      )}

      {/* RESULT MODAL */}
      {result && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full">
            <h2 className="text-xl font-bold">Result</h2>
            <p className="text-sm mt-3">Sent: {result.sent_count}</p>
            {result.skipped_count > 0 && <p>Skipped: {result.skipped_count}</p>}
            {result.failed_count > 0 && <p>Failed: {result.failed_count}</p>}

            <button
              onClick={() => setResult(null)}
              className="mt-5 w-full bg-blue-600 text-white py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
